/**
 * Sentry Error Tracking Configuration
 *
 * Captures:
 * - Unhandled JavaScript errors
 * - Unhandled promise rejections
 * - API errors (manual capture)
 * - React error boundaries
 *
 * Context enrichment:
 * - User country (USA/UAE)
 * - Current wizard step
 * - Dashboard tab
 * - Feature flags
 */

import * as Sentry from '@sentry/react'

// ============================================
// Types
// ============================================

export interface SentryContext {
  country?: 'USA' | 'UAE'
  wizardStep?: number
  dashboardTab?: string
  userId?: string
  email?: string
}

// ============================================
// Initialize Sentry
// ============================================

export function initSentry(dsn: string, environment: string = 'production') {
  if (!dsn) {
    // Only warn in development
    if (import.meta.env.DEV) {
      console.warn('[Sentry] No DSN provided, error tracking disabled')
    }
    return
  }

  // Validate DSN format (basic check)
  if (!dsn.startsWith('https://') || !dsn.includes('.sentry.io') && !dsn.includes('.ingest.')) {
    if (import.meta.env.DEV) {
      console.warn('[Sentry] Invalid DSN format, error tracking disabled')
    }
    return
  }

  try {
    Sentry.init({
    dsn,
    environment,

    // Performance monitoring
    tracesSampleRate: environment === 'production' ? 0.1 : 1.0,

    // Session replay (if you enable it later)
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0,

    // Release tracking
    release: import.meta.env.VITE_APP_VERSION || '1.0.0',

    // Ignore common non-actionable errors
    ignoreErrors: [
      // Browser extensions
      'top.GLOBALS',
      'canvas.contentDocument',
      'MyApp_RemoveAllHighlights',
      'atomicFindClose',
      // Network errors
      'Network request failed',
      'Failed to fetch',
      'Load failed',
      // Third-party scripts
      'Script error.',
      // React hydration (expected in some cases)
      'Hydration failed',
      // User-initiated navigation
      'AbortError',
      'The operation was aborted',
    ],

    // Filter out noisy URLs
    denyUrls: [
      // Browser extensions
      /extensions\//i,
      /^chrome:\/\//i,
      /^moz-extension:\/\//i,
      // Third-party scripts
      /googletagmanager\.com/i,
      /google-analytics\.com/i,
    ],

    // Before send hook for additional filtering
    beforeSend(event, hint) {
      const error = hint.originalException

      // Don't send rate limit errors (expected behavior)
      if (error instanceof Error && error.message.includes('rate limit')) {
        return null
      }

      // Add breadcrumb for API errors
      if (error instanceof Error && error.message.includes('/api/')) {
        event.tags = {
          ...event.tags,
          error_type: 'api_error',
        }
      }

      return event
    },

    // Add default tags
    initialScope: {
      tags: {
        app: 'nri-pathfinder',
      },
    },
  })

    // Only log in development
    if (import.meta.env.DEV) {
      console.log(`[Sentry] Initialized (${environment})`)
    }
  } catch (error) {
    // Silently fail in production, warn in development
    if (import.meta.env.DEV) {
      console.warn('[Sentry] Failed to initialize:', error)
    }
  }
}

// ============================================
// Context Management
// ============================================

/**
 * Set user context (call when user info is available)
 */
export function setSentryUser(user: {
  id?: string
  email?: string
  country?: 'USA' | 'UAE'
}) {
  Sentry.setUser({
    id: user.id,
    email: user.email,
  })

  if (user.country) {
    Sentry.setTag('country', user.country)
  }
}

/**
 * Set current context (wizard step, dashboard tab, etc.)
 */
export function setSentryContext(context: SentryContext) {
  Sentry.setContext('app_state', {
    country: context.country,
    wizard_step: context.wizardStep,
    dashboard_tab: context.dashboardTab,
  })

  if (context.country) {
    Sentry.setTag('country', context.country)
  }
  if (context.wizardStep) {
    Sentry.setTag('wizard_step', context.wizardStep.toString())
  }
  if (context.dashboardTab) {
    Sentry.setTag('dashboard_tab', context.dashboardTab)
  }
}

/**
 * Add breadcrumb for debugging
 */
export function addBreadcrumb(
  category: 'navigation' | 'api' | 'user_action' | 'state',
  message: string,
  data?: Record<string, unknown>
) {
  Sentry.addBreadcrumb({
    category,
    message,
    data,
    level: 'info',
  })
}

// ============================================
// Manual Error Capture
// ============================================

/**
 * Capture API errors with context
 */
export function captureAPIError(
  endpoint: string,
  error: Error | string,
  context?: {
    method?: string
    status?: number
    duration?: number
    requestBody?: Record<string, unknown>
  }
) {
  const errorObj = typeof error === 'string' ? new Error(error) : error

  Sentry.withScope((scope) => {
    scope.setTag('error_type', 'api_error')
    scope.setTag('endpoint', endpoint)

    if (context?.status) {
      scope.setTag('http_status', context.status.toString())
    }

    scope.setContext('api_request', {
      endpoint,
      method: context?.method || 'POST',
      status: context?.status,
      duration_ms: context?.duration,
      request_body: context?.requestBody
        ? JSON.stringify(context.requestBody).substring(0, 1000)
        : undefined,
    })

    Sentry.captureException(errorObj)
  })
}

/**
 * Capture Journey-specific errors
 */
export function captureJourneyError(
  phase: 'generation' | 'validation' | 'rendering',
  error: Error | string,
  context?: {
    country?: 'USA' | 'UAE'
    cached?: boolean
    schemaVersion?: string
  }
) {
  const errorObj = typeof error === 'string' ? new Error(error) : error

  Sentry.withScope((scope) => {
    scope.setTag('error_type', 'journey_error')
    scope.setTag('journey_phase', phase)

    if (context?.country) {
      scope.setTag('country', context.country)
    }

    scope.setContext('journey', {
      phase,
      country: context?.country,
      cached: context?.cached,
      schema_version: context?.schemaVersion,
    })

    // Set higher level for journey errors (premium feature)
    scope.setLevel('error')

    Sentry.captureException(errorObj)
  })
}

/**
 * Capture calculation errors
 */
export function captureCalculationError(
  calculator: 'fire' | 'rnor' | 'tax' | 'projection',
  error: Error | string,
  inputs?: Record<string, unknown>
) {
  const errorObj = typeof error === 'string' ? new Error(error) : error

  Sentry.withScope((scope) => {
    scope.setTag('error_type', 'calculation_error')
    scope.setTag('calculator', calculator)

    // Sanitize inputs (remove PII)
    const sanitizedInputs = inputs
      ? Object.fromEntries(
          Object.entries(inputs).filter(
            ([key]) => !['email', 'name', 'phone'].includes(key.toLowerCase())
          )
        )
      : undefined

    scope.setContext('calculation', {
      calculator,
      inputs: sanitizedInputs
        ? JSON.stringify(sanitizedInputs).substring(0, 1000)
        : undefined,
    })

    Sentry.captureException(errorObj)
  })
}

// ============================================
// Performance Monitoring
// ============================================

/**
 * Start a performance transaction
 */
export function startTransaction(name: string, op: string) {
  return Sentry.startInactiveSpan({
    name,
    op,
  })
}

// ============================================
// Re-export Sentry for direct access
// ============================================

export { Sentry }
